import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { Paging } from "types/common.types";

import { SelectOptions } from "components/Common/Select";

import { LiveRoomAccessDetails } from "store/meetings/types";
import { RootState } from "store/store";
import {
  createMeetingVoting,
  getAllVotings,
  getUserBallots,
  getUserVotings,
  getVoting,
  getVotingAccess,
  getVotingSecrecy,
  getVotingTypes,
} from "store/voting/actions";

import {
  deleteVoting,
  getVotingQuorumTypes,
  postVoting,
  putVoting,
  putVotingNote,
} from "./actions";
import { SingleVoting, UserBallots } from "./types";

export type VotingState = {
  error: any;
  loading: boolean;
  quorumTypes: SelectOptions[];
  userBallots: UserBallots[];
  votingTypes: SelectOptions[];
  votingSecrecy: SelectOptions[];
  liveRoomAccessDetails: LiveRoomAccessDetails | null;
  meta: Paging;
  counts: Record<string, number | undefined>;
};

const initialState: VotingState = {
  loading: false,
  error: false,
  quorumTypes: [],
  userBallots: [],
  votingTypes: [],
  votingSecrecy: [],
  liveRoomAccessDetails: null,
  meta: {} as Paging,
  counts: {},
};

export const votingAdapter = createEntityAdapter<SingleVoting>();

export const votingSlice = createSlice({
  name: "voting",
  initialState: votingAdapter.getInitialState<VotingState>(initialState),
  reducers: {
    votingSetAll: votingAdapter.setAll,
    votingAdd: votingAdapter.addOne,
    votingRemove: votingAdapter.removeOne,
    votingUpdate: votingAdapter.updateOne,
    votingAddOne: votingAdapter.addOne,
  },
  extraReducers: (builder) => {
    // POST VOTING
    builder.addCase(postVoting.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(postVoting.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(postVoting.fulfilled, (state) => {
      state.loading = false;
      state.error = false;
    });

    //PUT VOTING
    builder.addCase(putVoting.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(putVoting.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(putVoting.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      votingAdapter.upsertOne(state, action.payload.data);
    });

    //DELETE VOTING
    builder.addCase(deleteVoting.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(deleteVoting.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(deleteVoting.fulfilled, (state) => {
      state.loading = false;
      state.error = false;
    });
    //GET SINGLE VOTING
    builder.addCase(getVoting.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getVoting.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getVoting.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      votingAdapter.setOne(state, action.payload.data);
    });

    //get all votings
    builder.addCase(getAllVotings.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getAllVotings.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getAllVotings.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.meta = action.payload.meta;
      state.counts = action.payload.counts;
      votingAdapter.setAll(state, action.payload.data);
    });

    //quorum types
    builder.addCase(getVotingQuorumTypes.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getVotingQuorumTypes.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getVotingQuorumTypes.fulfilled, (state, action) => {
      state.error = false;
      state.loading = false;
      state.quorumTypes = action.payload.data;
    });

    //voting types
    builder.addCase(getVotingTypes.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getVotingTypes.pending, (state) => {
      state.error = false;
    });
    builder.addCase(getVotingTypes.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.votingTypes = action.payload.data;
    });

    //voting secrecy
    builder.addCase(getVotingSecrecy.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getVotingSecrecy.pending, (state) => {
      state.error = false;
    });
    builder.addCase(getVotingSecrecy.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.votingSecrecy = action.payload.data;
    });

    //create meeting voting
    builder.addCase(createMeetingVoting.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(createMeetingVoting.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(createMeetingVoting.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      votingAdapter.addOne(state, action.payload.data);
    });

    //get user votings
    builder.addCase(getUserVotings.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getUserVotings.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getUserVotings.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.meta = action.payload.meta;
      state.counts = action.payload.counts;
      votingAdapter.setAll(state, action.payload.data);
    });

    //get user ballots
    builder.addCase(getUserBallots.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getUserBallots.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getUserBallots.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.userBallots = action.payload.data;
    });

    //voting access details
    builder.addCase(getVotingAccess.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getVotingAccess.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getVotingAccess.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.liveRoomAccessDetails = action.payload;
    });

    //PUT VOTING NOTE
    builder.addCase(putVotingNote.rejected, (state, action) => {
      state.error = action.payload;
    });
    builder.addCase(putVotingNote.pending, (state) => {
      state.error = false;
    });
    builder.addCase(putVotingNote.fulfilled, (state, action) => {
      state.error = false;
    });
  },
});

export const { selectAll: selectAllVotings, selectById: selectVotingById } =
  votingAdapter.getSelectors<RootState>((state) => state.votings);
export const votingsLoading = (state: RootState) => state.votings.loading;

export default votingSlice.reducer;
